import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { mediaUrl } from "@/lib/pricing";

export const PhotoLightbox = ({ images, title, start = 0, onClose }) => {
  const [i, setI] = useState(start);
  const touch = useRef(null);
  const n = images.length;
  const prev = useCallback(() => setI((x) => (x - 1 + n) % n), [n]);
  const next = useCallback(() => setI((x) => (x + 1) % n), [n]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey, true); document.body.style.overflow = prevOverflow; };
  }, [onClose, prev, next]);

  useEffect(() => {
    document.querySelector(`[data-testid="lightbox-thumb-${i}"]`)?.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
  }, [i]);

  const arrow = "absolute top-1/2 -translate-y-1/2 h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur-md transition-colors";

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-slate-950/95 flex flex-col" onClick={onClose} data-testid="photo-lightbox">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0">
          <p className="font-bold truncate">{title}</p>
          <p className="text-xs text-white/60" data-testid="lightbox-counter">Foto {i + 1} de {n}</p>
        </div>
        <button onClick={onClose} aria-label="Fechar galeria" className="h-10 w-10 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center shrink-0" data-testid="lightbox-close">
          <X size={20} />
        </button>
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2 sm:px-20"
        onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onClose(); }}
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current === null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(dx) > 40) (dx > 0 ? prev : next)();
          touch.current = null;
        }}>
        <img key={i} src={mediaUrl(images[i])} alt={`${title} — foto ${i + 1}`}
          className="max-h-full max-w-full object-contain rounded-xl select-none animate-in fade-in duration-300" data-testid="lightbox-image" />
        {n > 1 && (
          <>
            <button onClick={prev} aria-label="Foto anterior" className={`${arrow} left-2 sm:left-6`} data-testid="lightbox-prev"><ChevronLeft size={24} /></button>
            <button onClick={next} aria-label="Próxima foto" className={`${arrow} right-2 sm:right-6`} data-testid="lightbox-next"><ChevronRight size={24} /></button>
          </>
        )}
      </div>
      {n > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 py-4 justify-start sm:justify-center" onClick={(e) => e.stopPropagation()}>
          {images.map((src, j) => (
            <button key={j} onClick={() => setI(j)} data-testid={`lightbox-thumb-${j}`}
              className={`shrink-0 h-14 w-20 sm:h-16 sm:w-24 rounded-lg overflow-hidden ring-2 transition-[opacity,box-shadow] ${j === i ? "ring-[#FFCC00] opacity-100" : "ring-transparent opacity-50 hover:opacity-90"}`}>
              <img src={mediaUrl(src)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};
